import MLTab from '../components/ui/MLTab';
import ModelsRiskTab from '../components/ui/ModelsRiskTab';
import NewsTab from '../components/ui/NewsTab';
import FilingsTab from '../components/ui/FilingsTab';
import ValuationTab from '../components/ui/ValuationTab';
import BusinessTab from '../components/ui/BusinessTab';
import PriceTab from '../components/ui/PriceTab';
import SummaryTab from '../components/ui/SummaryTab';
import BreakthroughsPanel from '../components/ui/BreakthroughsPanel';
import LoadingSnapshot from '../components/ui/LoadingSnapshot';
// ============================================================
// QuantEdge v6.0 — Dashboard
// PUBLIC — no login required to analyze
// Login only to save watchlist/portfolio
// ============================================================

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore, api } from '../auth/authStore';
import toast from 'react-hot-toast';
import {
  WallStreetPanel,
  PortfolioPanel,
  PerformancePanel,
  MLModelsPanel,
  VolatilityPanel,
  RegimePanel,
  OptionsPanel,
  SentimentPanel,
  ScenarioPanel,
  Watchlist,
} from '../components/ui';
import PriceChart from '../components/charts/PriceChart';
import Screener from './Screener';
import OverviewV2 from './overview_v2/OverviewV2';
import PatternLab from '../components/ui/PatternLab';
import CompanyIntel from '../components/ui/CompanyIntel';
import NewsPanel from '../components/ui/NewsPanel';
import RiskPanel from '../components/ui/RiskPanel';
import QuartersPanel from '../components/ui/QuartersPanel';
import ClaimPanel from '../components/ui/ClaimPanel';
import HoldersPanel from '../components/ui/HoldersPanel';
import IndustryPanel from '../components/ui/IndustryPanel';
import CompetitivePanel from '../components/ui/CompetitivePanel';
import ManagementPanel from '../components/ui/ManagementPanel';
import OwnershipPanel from '../components/ui/OwnershipPanel';
import MacroPanel from '../components/ui/MacroPanel';
import ForecastPanel from '../components/ui/ForecastPanel';
import AltDataPanel from '../components/ui/AltDataPanel';
import IFlowPanel from '../components/ui/IFlowPanel';
import PeerPanel from '../components/ui/PeerPanel';
import FinancialIntelligencePanel from '../components/ui/FinancialIntelligencePanel';
import ConvictionBadge from '../components/ui/ConvictionBadge';
import ValuationPanel from '../components/ui/ValuationPanel';
import MarketPanel from '../components/ui/MarketPanel';
import BusinessPanel from '../components/ui/BusinessPanel';

const TABS = [
  { id: 'overview',     label: '⬡ SUMMARY' },
  { id: 'patterns',    label: '📈 PRICE & PATTERNS' },
  { id: 'intel',       label: '🔎 FILINGS, OWNERS & ANALYSTS' },
  { id: 'valuation',   label: '⚖️ VALUATION & PEERS' },
  { id: 'business',    label: '🏰 BUSINESS' },
  { id: 'news',        label: '📰 NEWS' },
  { id: 'risk',        label: '🛡 RISK & SCORE' },
  { id: 'ml',          label: '🧠 ML MODELS' },
];

const QUICK_TICKERS = ['AAPL', 'NVDA', 'TSLA', 'SPY', 'QQQ', 'MSFT', 'AMZN', 'META', 'GOOGL', 'BRK-B'];

export default function Dashboard() {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [ticker, setTicker] = useState('');
  const [inputTicker, setInputTicker] = useState('');

  // Auto-run analysis if ?ticker=XXX in URL.
  // Runs on initial mount AND on URL param change — so clicking "Full
  // analysis" from the Screener tab (same-page nav to /dashboard?ticker=X)
  // correctly re-triggers the analysis instead of being a no-op.
  useEffect(() => {
    const urlTicker = searchParams.get('ticker');
    if (!urlTicker || !urlTicker.trim()) {
      // No ticker and nothing running: this page has no job to do. The idle
      // state used to duplicate the landing page (with a stale model grid
      // that still advertised LSTM+TFT and options GEX months after both
      // were removed) — one front door, one place for that copy to live.
      if (!ticker && !data && !loading) navigate('/', { replace: true });
      return;
    }
    const sym = urlTicker.toUpperCase().trim();

    // Skip if we're already analyzing this ticker (prevents duplicate fires
    // on state-triggered re-renders).
    if (sym === ticker) return;

    setInputTicker(sym);
    const timer = setTimeout(() => {
      setTicker(sym);
      setData(null);
      setLoading(true);
      setElapsed(0);
      setActiveTab('overview');
      api.post('/api/v6/analyze', {
        req: { ticker: sym, include_options: false, include_sentiment: true, mc_paths: 10000 }
      }).then(res => {
        setData(res.data.data);
        toast.success(`Analysis complete: ${sym}`, { icon: '✅' });
      }).catch(() => {
        toast.error('Analysis failed');
      }).finally(() => {
        setLoading(false);
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchParams]); // eslint-disable-line
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [notCompany, setNotCompany] = useState<string|null>(null);
  const timerRef = useRef<any>(null);

  const runAnalysis = useCallback(async (sym?: string) => {
    const symbol = (sym || inputTicker).toUpperCase().trim();
    if (!symbol) return;
    setTicker(symbol);
    setInputTicker(symbol);
    setData(null);
    setLoading(true);
    setElapsed(0);
    setActiveTab('overview');
    // Company pages are for stocks in the universe; a fund stops here with an explanation.
    try {
      const qk = await api.get(`/api/v6/quick/${symbol}`);
      if (qk.data && qk.data.is_company === false) { setNotCompany(qk.data.message); setLoading(false); return; }
    } catch { /* snapshot unavailable: proceed with the analysis */ }
    setNotCompany(null);

    // Progress messages
    const msgs = [
      'Fetching 5Y price history...',
      'Computing 152 features...',
      'Running GARCH volatility model...',
      'Running HMM regime classifier...',
      'Running XGBoost + LightGBM panel (6 horizons)...',
      'Running FinBERT sentiment...',
      'Running Monte Carlo (10K paths, Student-t)...',
      'Running risk engine (CVaR, position sizing)...',
      'Assembling institutional report...',
    ];
    let msgIdx = 0;
    setLoadingMsg(msgs[0]);
    const msgTimer = setInterval(() => {
      msgIdx = (msgIdx + 1) % msgs.length;
      setLoadingMsg(msgs[msgIdx]);
    }, 3200);

    // Elapsed timer
    const start = Date.now();
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);

    try {
      const res = await api.post('/api/v6/analyze', {
        req: {
          ticker: symbol,
          include_options: false,   // Polygon plan returns 403 on the options snapshot
          include_sentiment: true,
          mc_paths: 10000,
        }
      });
      setData(res.data.data);
      toast.success(`Analysis complete: ${symbol}`, { icon: '✅' });
    } catch (err: any) {
      // Analysis is public — never bounce to login on auth errors.
      const msg = err?.response?.data?.detail || 'Analysis failed';
      toast.error(msg);
    } finally {
      clearInterval(msgTimer);
      clearInterval(timerRef.current);
      setLoading(false);
    }
  }, [inputTicker, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const signal = data?.overall_signal || 'NEUTRAL';
  const signalColor = signal.includes('BUY') ? '#22c55e' : signal.includes('SELL') ? '#ef4444' : '#f59e0b';

  // ── QuantEdge Conviction (16-module) — the single authoritative score ──
  const [conviction, setConviction] = useState<{score:number; verdict:string} | null>(null);
  useEffect(() => {
    if (!ticker) { setConviction(null); return; }
    api.get(`/api/v7/conviction/${ticker}`)
      .then(r => { const d = r.data?.data; if (d && d.conviction_score != null) setConviction({ score: d.conviction_score, verdict: d.verdict || 'NEUTRAL' }); })
      .catch(() => setConviction(null));
  }, [ticker, data]);
  const convScore = conviction ? Math.round(conviction.score) : null;
  const convVerdict = 'SCORE';   // no BUY/SELL verdict until the score has a measured track record
  const convColor = '#9d8b7a';

  return (
    <div style={{ minHeight: '100vh', background: '#1a0f0a', fontFamily: "'Outfit', sans-serif", color: '#f4e8d8' }}>
      
      {/* ── Header ── */}
      <header style={{
        borderBottom: '1px solid rgba(212,149,108,0.12)',
        background: 'rgba(26,15,10,0.95)',
        backdropFilter: 'blur(20px)',
        position: 'sticky', top: 0, zIndex: 100,
        padding: '0 20px',
      }}>
        <div style={{ maxWidth: 2100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 16, height: 56 }}>
          
          {/* Logo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div onClick={() => { window.location.href = '/'; }} title="Home" role="link" style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: 22, letterSpacing: 5, color: '#daa520',
            }}>QUANTEDGE</div>
            <div style={{
              fontFamily: "'Fira Code', monospace",
              fontSize: 8, color: '#8a7560', letterSpacing: 2, paddingTop: 2,
            }}>v6.0</div>
            {data && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 4 }}>
                <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e', animation: 'pulse 1.5s infinite' }} />
                <span style={{ fontFamily: "'Fira Code',monospace", fontSize: 8, color: '#22c55e', letterSpacing: 1 }}>LIVE</span>
              </div>
            )}
          </div>

          {/* Search bar */}
          <div style={{ flex: 1, maxWidth: 480, display: 'flex', gap: 8 }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <input
                value={inputTicker}
                onChange={e => setInputTicker(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && runAnalysis()}
                placeholder="Enter ticker  e.g. AAPL, NVDA, SPY..."
                style={{
                  width: '100%', background: '#241510',
                  border: '1px solid rgba(212,149,108,0.2)', borderRadius: 6,
                  color: '#f4e8d8', fontFamily: "'Fira Code', monospace",
                  fontSize: 12, padding: '8px 12px', outline: 'none',
                }}
              />
            </div>
            <button
              onClick={() => runAnalysis()}
              disabled={loading || !inputTicker}
              style={{
                background: loading ? '#3a2920' : 'linear-gradient(135deg,#daa520,#b8860b)',
                color: '#1a0f0a', fontFamily: "'Fira Code',monospace",
                fontWeight: 700, fontSize: 10, letterSpacing: 2,
                padding: '8px 18px', border: 'none', borderRadius: 6,
                cursor: loading || !inputTicker ? 'not-allowed' : 'pointer',
                opacity: !inputTicker ? 0.5 : 1,
                transition: 'all 0.15s', whiteSpace: 'nowrap',
              }}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ width: 10, height: 10, border: '2px solid #8a7560', borderTopColor: '#daa520', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
                  {elapsed}s
                </span>
              ) : 'ANALYZE →'}
            </button>
          </div>

          {/* Quick tickers */}
          <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap', overflow: 'hidden' }}>
            {QUICK_TICKERS.slice(0, 6).map(t => (
              <button key={t} onClick={() => runAnalysis(t)}
                style={{
                  background: ticker === t ? 'rgba(218,165,32,0.15)' : 'transparent',
                  border: `1px solid ${ticker === t ? '#daa520' : 'rgba(212,149,108,0.15)'}`,
                  color: ticker === t ? '#daa520' : '#9d8b7a',
                  fontFamily: "'Fira Code',monospace", fontSize: 9,
                  padding: '4px 8px', borderRadius: 4, cursor: 'pointer',
                  transition: 'all 0.12s',
                }}
              >{t}</button>
            ))}
          </div>

          {/* Right side */}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
            {data && (
              <div style={{
                fontFamily: "'Fira Code',monospace", fontSize: 9,
                color: signalColor, letterSpacing: 2,
                background: `${signalColor}15`, padding: '4px 10px',
                border: `1px solid ${signalColor}40`, borderRadius: 4,
              }}>
                {convVerdict} · {convScore != null ? convScore : (data.overall_score || 50)}
              </div>
            )}
            <button onClick={() => navigate('/')}
              onMouseEnter={(e) => { const b = e.currentTarget; b.style.background = 'rgba(218,165,32,0.12)'; b.style.borderColor = '#daa520'; b.style.color = '#daa520'; const a = b.querySelector('span'); if (a) a.style.transform = 'translateX(-3px)'; }}
              onMouseLeave={(e) => { const b = e.currentTarget; b.style.background = 'rgba(218,165,32,0.04)'; b.style.borderColor = 'rgba(218,165,32,0.35)'; b.style.color = '#c9a227'; const a = b.querySelector('span'); if (a) a.style.transform = 'translateX(0)'; }}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(218,165,32,0.04)', border: '1px solid rgba(218,165,32,0.35)', color: '#c9a227', fontFamily: "'Fira Code',monospace", fontWeight: 600, fontSize: 9, padding: '5px 11px', borderRadius: 4, cursor: 'pointer', letterSpacing: 1, transition: 'all 0.18s ease' }}>
              <span style={{ display: 'inline-block', transition: 'transform 0.18s ease' }}>←</span> HOME
            </button>
            {isAuthenticated ? (
              <button onClick={handleLogout}
                style={{ background: 'none', border: '1px solid rgba(212,149,108,0.2)', color: '#9d8b7a', fontFamily: "'Fira Code',monospace", fontSize: 9, padding: '5px 10px', borderRadius: 4, cursor: 'pointer', letterSpacing: 1 }}>
                LOGOUT
              </button>
            ) : (
              <button onClick={() => navigate('/login')}
                style={{ background: 'linear-gradient(135deg,#daa520,#b8860b)', border: 'none', color: '#1a0f0a', fontFamily: "'Fira Code',monospace", fontWeight: 700, fontSize: 9, padding: '5px 12px', borderRadius: 4, cursor: 'pointer', letterSpacing: 1 }}>
                LOGIN →
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ── Loading overlay ── */}
      {loading && <LoadingSnapshot ticker={ticker} elapsed={elapsed} />}
      {!loading && notCompany && (
        <div style={{ maxWidth: 720, margin: '80px auto', padding: 28, background: '#1c130e', border: '1px solid #3a2920', borderRadius: 12, textAlign: 'center' }}>
          <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 22, color: '#daa520', marginBottom: 14 }}>{ticker}</div>
          <div style={{ fontSize: 15.5, lineHeight: 1.7, color: '#d4c4b0' }}>{notCompany}</div>
          <button onClick={() => navigate('/')} style={{ marginTop: 22, background: 'none', border: '1px solid #daa520', color: '#daa520', borderRadius: 6, padding: '10px 18px', fontFamily: "'Fira Code',monospace", fontSize: 11, letterSpacing: 1.5, cursor: 'pointer' }}>SEARCH A COMPANY →</button>
        </div>)}

      <main style={{ maxWidth: 2100, margin: '0 auto', padding: '16px 28px' }}>

        {/* ── Data loaded ── */}
        {data && (
          <>
            {/* ── Ticker header ── */}
            <TickerHeader data={data} ticker={ticker} />
            <StockSnapshot data={data} ticker={ticker} />

            {/* ── Tabs ── */}
            <div className="qe-tabscroll" style={{ borderBottom: '1px solid rgba(212,149,108,0.12)', marginBottom: 20 }}>
              {TABS.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  style={{
                    fontFamily: "'Fira Code',monospace", fontSize: 9, letterSpacing: 1.5,
                    padding: '10px 14px', background: 'none', border: 'none',
                    borderBottom: `2px solid ${activeTab === tab.id ? '#daa520' : 'transparent'}`,
                    color: activeTab === tab.id ? '#daa520' : '#9d8b7a',
                    cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.12s',
                  }}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ── Tab content ── */}
            <div style={{ animation: 'fadeIn 0.3s ease' }}>
              {activeTab === 'overview'    && <SummaryTab ticker={ticker} data={data} macro={<MacroPanel ticker={ticker} />} />}
              
              {['patterns','market','volatility','regime'].includes(activeTab) && <PriceTab ticker={ticker} data={data} states={<RegimePanel data={data} />} />}
              {['intel','ownership','iflow','wallstreet'].includes(activeTab) && <FilingsTab ticker={ticker} filings={<><BreakthroughsPanel ticker={ticker} /><CompanyIntel ticker={ticker} /></>} holders={<HoldersPanel ticker={ticker} />} flow={<IFlowPanel ticker={ticker} />} />}
              
              
              {['news','altdata'].includes(activeTab) && <NewsTab ticker={ticker} fullModel={<NewsPanel ticker={ticker} data={data} />} />}
              
              {activeTab === 'ml' && <MLTab ticker={ticker} data={data} />}
          {['risk','forecast','portfolio','performance'].includes(activeTab) && <ModelsRiskTab ticker={ticker} data={data} forward={<ForecastPanel ticker={ticker} />} riskModel={<RiskPanel ticker={ticker} />} />}
              
              
              
              
              {activeTab === 'macro'       && <MacroPanel ticker={ticker} />}
              
              
              
              
              {['valuation','peers'].includes(activeTab) && <ValuationTab ticker={ticker} peersChart={<PeerPanel data={data} ticker={ticker} onAnalyze={runAnalysis} lite />} fullModel={<ValuationPanel ticker={ticker} />} />}
              
              {['business','financial','management','competitive','industry'].includes(activeTab) && <BusinessTab ticker={ticker} sections={[
                { id: 'quarters', title: 'Quarterly results', hint: 'up to 20 quarters, each vs the same quarter a year earlier', open: true, node: <QuartersPanel ticker={ticker} /> },
                { id: 'quality', title: 'Financial quality', hint: 'profitability, cash flow, balance sheet — every score explained', node: <FinancialIntelligencePanel ticker={ticker} /> },
                { id: 'moat', title: 'Moat & business model', hint: 'how durable its advantage looks in the numbers', node: <BusinessPanel ticker={ticker} /> },
                { id: 'mgmt', title: 'Management & capital allocation', hint: 'buybacks, dividends, insiders, reinvestment', node: <ManagementPanel ticker={ticker} /> },
                { id: 'comp', title: 'Competition', hint: 'how it compares with close rivals', node: <CompetitivePanel ticker={ticker} /> },
                { id: 'ind', title: 'Industry', hint: 'its sector, and how it moves with it — beta only means something when correlation is meaningful', node: <IndustryPanel ticker={ticker} /> },
              ]} />}
              
              
              
              
            </div>
          </>
        )}
      </main>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Outfit:wght@300;400;600;800&family=Fira+Code:wght@400;600&display=swap');
        @keyframes fadeIn { from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:none;} }
        @keyframes pulse { 0%,100%{opacity:1;}50%{opacity:0.3;} }
        @keyframes spin { to{transform:rotate(360deg);} }
        * { scrollbar-width: thin; scrollbar-color: #3a2920 #1a0f0a; }
        input::placeholder { color: #8a7560; }
        input:focus { border-color: rgba(218,165,32,0.5) !important; outline: none; }
      `}</style>
    </div>
  );
}

// ── Ticker Header ─────────────────────────────────────────────
function TickerHeader({ data, ticker }: { data: any; ticker: string }) {
  const change = data.change || 0;
  const changePct = data.change_pct || 0;
  const isUp = change >= 0;
  const signal = data.overall_signal || 'NEUTRAL';
  const signalColor = signal.includes('BUY') ? '#22c55e' : signal.includes('SELL') ? '#ef4444' : '#f59e0b';
  // Conviction (16-module) — the authoritative score
  const [conviction, setConviction] = useState<{score:number; verdict:string} | null>(null);
  useEffect(() => {
    if (!ticker) { setConviction(null); return; }
    api.get(`/api/v7/conviction/${ticker}`)
      .then(r => { const d = r.data?.data; if (d && d.conviction_score != null) setConviction({ score: d.conviction_score, verdict: d.verdict || 'NEUTRAL' }); })
      .catch(() => setConviction(null));
  }, [ticker, data]);
  const convScore = conviction ? Math.round(conviction.score) : null;
  const convVerdict = conviction ? conviction.verdict.replace('_',' ') : signal;
  const convColor = convVerdict.includes('BUY') ? '#22c55e' : convVerdict.includes('SELL') ? '#ef4444' : '#f59e0b';

  return (
    <div style={{
      background: '#241510', border: '1px solid rgba(212,149,108,0.12)',
      borderRadius: 8, padding: '16px 20px', marginBottom: 20,
      display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center',
    }}>
      {/* Ticker + name */}
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 28, letterSpacing: 4, color: '#daa520' }}>{ticker}</span>
          <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: 13, color: '#9d8b7a' }}>{data.name || ''}</span>
        </div>
        <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 9, color: '#8a7560', letterSpacing: 2 }}>
          {[data.exchange, data.sector, data.industry].filter((v: any, i: number, a: any[]) => v && a.indexOf(v) === i).join(' · ')}
        </div>
      </div>

      {/* Price */}
      <div>
        <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 26, fontWeight: 700, color: '#f4e8d8' }}>
          ${data.price?.toFixed(2) || '—'}
        </div>
        <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 11, color: isUp ? '#22c55e' : '#ef4444' }}>
          {isUp ? '+' : ''}{change.toFixed(2)} ({isUp ? '+' : ''}{changePct.toFixed(2)}%)
        </div>
      </div>

      {/* Signal */}

      {/* QuantEdge Conviction (new 20-module consolidated score) */}
      <ConvictionBadge ticker={ticker} />

      {/* Key stats */}
      <div style={{ display: 'flex', gap: 20, marginLeft: 'auto', flexWrap: 'wrap' }}>
        {[
          { label: 'MARKET VALUE', value: formatLarge(data.market_cap) },
          { label: 'VS 52W HIGH', value: (data.week_52_high && data.price) ? `${((data.price / data.week_52_high - 1) * 100).toFixed(1)}%` : '—' },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 8, color: '#8a7560', letterSpacing: 2, marginBottom: 2 }}>{s.label}</div>
            <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 12, color: '#d4c4b0', fontWeight: 600 }}>{s.value}</div>
          </div>
        ))}
        <PeStat ticker={ticker} mcap={data.market_cap} />
        <VolStat ticker={ticker} />
      </div>


    </div>
  );
}

// P/E from the last four reported quarters (SEC), not a year-old annual figure.
function PeStat({ ticker, mcap }: { ticker: string; mcap?: number }) {
  const [f, setF] = useState<any>(null);
  useEffect(() => { setF(null); api.get(`/api/v6/summary/${ticker}`).then(r => setF(r.data?.facts || null)).catch(() => {}); }, [ticker]);
  const pe = f?.ni_ttm > 0 && mcap ? mcap / f.ni_ttm : null;
  return (<div style={{ textAlign: 'center' }}>
    <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 8, color: '#8a7560', letterSpacing: 2, marginBottom: 2 }}>P/E · 12M</div>
    <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 12, color: '#d4c4b0', fontWeight: 600 }}>{pe != null ? `${pe.toFixed(1)}×` : f && !(f.ni_ttm > 0) ? 'loss' : '—'}</div></div>);
}

// Volatility from the single price-stats source, with its window stated.
function VolStat({ ticker }: { ticker: string }) {
  const [v, setV] = useState<any>(null);
  useEffect(() => { setV(null); api.get(`/api/v6/price-stats/${ticker}`).then(r => setV(r.data)).catch(() => {}); }, [ticker]);
  return (<div style={{ textAlign: 'center' }}>
    <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 8, color: '#8a7560', letterSpacing: 2, marginBottom: 2 }}>VOLATILITY · 12M</div>
    <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 12, color: '#d4c4b0', fontWeight: 600 }}>{v?.vol_1y != null ? `${(v.vol_1y * 100).toFixed(1)}%` : '—'}</div></div>);
}

// ── Institutional Snapshot (fundamentals · valuation · positioning · street) ──
function StockSnapshot({ data, ticker }: { data: any; ticker?: string }) {
  // Growth and margins from the company's own SEC filings (same source as the Summary),
  // so the page never shows two different numbers for the same thing.
  const [sec, setSec] = useState<any>(null);
  useEffect(() => { setSec(null); if (!ticker) return; api.get(`/api/v6/summary/${ticker}`).then(r => setSec(r.data?.facts || null)).catch(() => {}); }, [ticker]);
  const f0 = data.fundamentals || {};
  const f = sec ? { ...f0, revenue_ttm: sec.revenue_ttm ?? f0.revenue_ttm, revenue_growth: sec.sales_yoy ?? f0.revenue_growth,
                    gross_margin: sec.gross_margin_ttm ?? f0.gross_margin, net_margin: sec.net_margin_ttm ?? f0.net_margin } : f0;
  const ar = data.analyst_ratings || {};
  const cons = ar.consensus || {};
  const price = data.price ?? 0;
  const hi = data.week_52_high ?? f.week_52_high;
  const lo = data.week_52_low ?? f.week_52_low;
  const rangePos = (hi && lo && hi > lo) ? Math.max(0, Math.min(100, ((price - lo) / (hi - lo)) * 100)) : null;

  const fmtPct = (v:any) => v == null ? '—' : `${(Number(v)*100).toFixed(1)}%`;
  const fmtX = (v:any) => v == null ? '—' : `${Number(v).toFixed(1)}x`;
  const fmtBig = (v:any) => { if (v == null) return '—'; const n=Number(v); if(n>=1e12)return `$${(n/1e12).toFixed(2)}T`; if(n>=1e9)return `$${(n/1e9).toFixed(1)}B`; if(n>=1e6)return `$${(n/1e6).toFixed(0)}M`; return `$${n.toFixed(0)}`; };
  const col = (good:boolean|null) => good == null ? '#d4c4b0' : good ? '#22c55e' : '#ef4444';

  const Cell = ({label, value, color}:{label:string; value:string; color?:string}) => (
    <div style={{ display:'flex', justifyContent:'space-between', padding:'3px 0', fontSize:11 }}>
      <span style={{ fontFamily:"'Outfit',sans-serif", color:'#8a7560' }}>{label}</span>
      <span style={{ fontFamily:"'Fira Code',monospace", color: value === '—' ? '#8a7560' : (color||'#d4c4b0'), fontWeight:600 }}>{value}</span>
    </div>
  );
  const ColHead = ({t}:{t:string}) => (
    <div style={{ fontFamily:"'Fira Code',monospace", fontSize:9, color:'#daa520', letterSpacing:2, marginBottom:6, borderBottom:'1px solid rgba(212,149,108,0.15)', paddingBottom:4 }}>{t}</div>
  );

  return (
    <div style={{ background:'#241510', border:'1px solid rgba(212,149,108,0.12)', borderRadius:8, padding:'16px 20px', marginBottom:20,
      display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px, 1fr))', gap:24 }}>
      <div>
        <ColHead t={sec ? `FUNDAMENTALS · SEC FILINGS${sec.last_quarter ? " · QTR TO " + sec.last_quarter : ""}` : "FUNDAMENTALS"} />
        <Cell label="Revenue TTM" value={fmtBig(f.revenue_ttm)} />
        <Cell label={sec ? "Sales growth (vs yr ago)" : "Rev Growth"} value={fmtPct(f.revenue_growth)} color={f.revenue_growth == null ? undefined : col(f.revenue_growth > 0)} />
        <Cell label={sec ? "Gross margin · 12 mo" : "Gross Margin"} value={fmtPct(f.gross_margin)} color={col(f.gross_margin>0.4)} />
        <Cell label={sec ? "Net margin · 12 mo" : "Net Margin"} value={fmtPct(f.net_margin)} color={col(f.net_margin>0.1)} />
        <Cell label="Operating margin · 12 mo" value={sec?.op_margin_ttm != null ? `${(sec.op_margin_ttm * 100).toFixed(1)}%` : "—"} />
        <Cell label="Liabilities / Equity" value={f.debt_to_equity != null ? Number(f.debt_to_equity).toFixed(2) : '—'} color={col(f.debt_to_equity != null ? f.debt_to_equity < 1.5 : null)} />
      </div>
      <div>
        <ColHead t="VALUATION" />
        <Cell label="P/E · last 12 mo" value={sec?.ni_ttm > 0 && data.market_cap ? `${(data.market_cap / sec.ni_ttm).toFixed(1)}x` : "—"} />
        <Cell label="P/S · last 12 mo" value={sec?.revenue_ttm && data.market_cap ? `${(data.market_cap / sec.revenue_ttm).toFixed(1)}x` : "—"} />
        <Cell label="FCF Yield" value={fmtPct(data.fcf_yield)} color={col(data.fcf_yield>0.04)} />
      </div>
      <div>
        <ColHead t="POSITIONING" />
        <Cell label="52W High" value={hi != null ? `$${Number(hi).toFixed(2)}` : '—'} />
        <Cell label="52W Low" value={lo != null ? `$${Number(lo).toFixed(2)}` : '—'} />
        <Cell label="Range Pos" value={rangePos != null ? `${rangePos.toFixed(0)}%` : '—'} color={rangePos != null ? (rangePos>70?'#22c55e':rangePos<30?'#ef4444':'#d4c4b0') : undefined} />
        {rangePos != null && (
          <div style={{ height:6, background:'#1a0f0a', borderRadius:3, overflow:'hidden', margin:'6px 0' }}>
            <div style={{ height:'100%', width:`${rangePos}%`, background:'linear-gradient(90deg,#8a7560,#daa520)', borderRadius:3 }} />
          </div>
        )}
      </div>
      <div>
        <ColHead t={`THE STREET · FINNHUB${(ar.period || cons.period) ? ' · ' + (ar.period || cons.period) : ''}`} />
        {cons && cons.n_analysts ? (
          <>
            <Cell label={`Buy (${cons.n_analysts} analysts)`} value={`${cons.buy_count ?? '—'}`} color="#22c55e" />
            <Cell label="Hold" value={`${cons.hold_count ?? '—'}`} color="#f59e0b" />
            <Cell label="Sell" value={`${cons.sell_count ?? '—'}`} color="#ef4444" />
            <Cell label="Consensus" value={cons.label || '—'} color={cons.label?.includes('BUY') ? '#22c55e' : cons.label?.includes('SELL') ? '#ef4444' : '#f59e0b'} />
            {(cons.upgrades_30d || cons.downgrades_30d) ? (
              <Cell label="30d Revisions" value={`+${cons.upgrades_30d||0} / -${cons.downgrades_30d||0}`} />
            ) : null}
          </>
        ) : (
          <div style={{ fontFamily:"'Outfit',sans-serif", fontSize:10, color:'#8a7560', lineHeight:1.5, paddingTop:4 }}>
            No analyst coverage available for this ticker.
          </div>
        )}
      </div>
    </div>
  );
}




function RegimeSummary({ data }: { data: any }) {
  const regime = data.regime || {};
  const probs = regime.regime_probabilities || {};
  const current = data.current_regime || 'UNKNOWN';
  const color = current.includes('BULL') ? '#22c55e' : current.includes('BEAR') ? '#ef4444' : '#f59e0b';
  return (
    <Panel title="HMM REGIME" icon="🌡">
      <div style={{ textAlign: 'center', padding: '8px 0 12px' }}>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 20, color, letterSpacing: 3 }}>
          {current.replace(/_/g, ' ')}
        </div>
        <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 9, color: '#9d8b7a' }}>
          {pct1(regime.confidence)} confidence · {Math.round(regime.expected_duration_days || 0)}d expected
        </div>
      </div>
      {Object.entries(probs).slice(0, 4).map(([name, p]: [string, any]) => (
        <div key={name} style={{ marginBottom: 5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
            <span style={{ fontFamily: "'Fira Code',monospace", fontSize: 8, color: name === current ? '#daa520' : '#9d8b7a' }}>{name.replace(/_/g, ' ')}</span>
            <span style={{ fontFamily: "'Fira Code',monospace", fontSize: 9, color: '#d4c4b0' }}>{pct1(p)}</span>
          </div>
          <div style={{ height: 3, background: '#1a0f0a', borderRadius: 2 }}>
            <div style={{ height: '100%', background: name === current ? color : '#3a2920', borderRadius: 2, width: `${(p || 0) * 100}%`, transition: 'width 1s ease' }} />
          </div>
        </div>
      ))}
    </Panel>
  );
}

function SentimentSummary({ data }: { data: any }) {
  const s = data.sentiment || {};
  const score = s.composite || 0;
  const color = score > 0.1 ? '#22c55e' : score < -0.1 ? '#ef4444' : '#f59e0b';
  const headlines = s.headlines || [];
  return (
    <Panel title="NLP SENTIMENT" icon="💬">
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        {[
          { label: 'NEWS', value: s.news?.score, sub: s.news?.label },
          { label: 'REDDIT', value: s.reddit?.score, sub: s.reddit?.label },
          { label: 'COMPOSITE', value: score, sub: s.label },
        ].map(item => (
          <div key={item.label} style={{ flex: 1, textAlign: 'center', background: '#1a0f0a', borderRadius: 6, padding: '8px 4px' }}>
            <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 7, color: '#9d8b7a', letterSpacing: 1, marginBottom: 4 }}>{item.label}</div>
            <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 13, color: (item.value ?? 0) > 0 ? '#22c55e' : '#ef4444', fontWeight: 700 }}>
              {item.value != null ? (item.value > 0 ? '+' : '') + item.value.toFixed(2) : '—'}
            </div>
            <div style={{ fontFamily: "'Fira Code',monospace", fontSize: 7, color: '#8a7560' }}>{item.sub || ''}</div>
          </div>
        ))}
      </div>
      {headlines.slice(0, 2).map((h: string, i: number) => (
        <div key={i} style={{ fontFamily: "'Outfit',sans-serif", fontSize: 10, color: '#9d8b7a', borderLeft: '2px solid #3a2920', paddingLeft: 8, marginBottom: 6, lineHeight: 1.4 }}>
          {h.slice(0, 80)}{h.length > 80 ? '...' : ''}
        </div>
      ))}
    </Panel>
  );
}

// ── Shared components ─────────────────────────────────────────
function Panel({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#241510', border: '1px solid rgba(212,149,108,0.1)', borderRadius: 8, padding: 16, height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, paddingBottom: 10, borderBottom: '1px solid rgba(212,149,108,0.08)' }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span style={{ fontFamily: "'Fira Code',monospace", fontSize: 9, color: '#9d8b7a', letterSpacing: 2 }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

function DataRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(212,149,108,0.06)' }}>
      <span style={{ fontFamily: "'Outfit',sans-serif", fontSize: 11, color: '#9d8b7a' }}>{label}</span>
      <span style={{ fontFamily: "'Fira Code',monospace", fontSize: 11, color: '#d4c4b0', fontWeight: 600 }}>{value}</span>
    </div>
  );
}

// ── Formatters ────────────────────────────────────────────────
function pct(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${(v * 100).toFixed(2)}%`;
}
function pct1(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${(v * 100).toFixed(1)}%`;
}
function num2(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toFixed(2);
}
function num3(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toFixed(3);
}
function formatLarge(v: number | null | undefined): string {
  if (!v) return '—';
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9)  return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6)  return `$${(v / 1e6).toFixed(1)}M`;
  return `$${v.toFixed(0)}`;
}
